import type { ReactNode } from 'react';

import { cn } from '@/utils/cls';

interface ColumnDef<Row> {
  key: string;
  header: ReactNode;
  /** Tailwind alignment / width classes for both <th> and <td>. */
  className?: string;
  /** Renderer for the cell. Defaults to `row[key]`. */
  cell?: (row: Row) => ReactNode;
  /** Hide on small screens — drops the column at <md. */
  hideOnMobile?: boolean;
}

interface TableProps<Row> {
  columns: ColumnDef<Row>[];
  rows: Row[];
  rowKey: (row: Row, idx: number) => string | number;
  onRowClick?: (row: Row) => void;
  empty?: ReactNode;
  loading?: boolean;
  /** Optional dense mode — tighter rows */
  dense?: boolean;
}

/**
 * Generic table. We wrap in `overflow-x-auto` so wide tables get a
 * horizontal scrollbar on mobile rather than blowing up the layout.
 */
export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  onRowClick,
  empty,
  loading = false,
  dense = false,
}: TableProps<Row>) {
  return (
    <div className="card p-0 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-surface-2 border-b border-line">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={cn(
                    'text-xs font-semibold text-ink-soft uppercase tracking-wide',
                    'px-4 py-3 text-left whitespace-nowrap',
                    c.hideOnMobile && 'hidden md:table-cell',
                    c.className,
                  )}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-12 text-center text-ink-soft">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-12 text-center text-ink-soft">
                  {empty ?? 'Nothing here yet.'}
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr
                  key={rowKey(row, idx)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    'border-b border-line last:border-0',
                    onRowClick && 'cursor-pointer hover:bg-surface-2',
                  )}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={cn(
                        'px-4 text-sm text-ink',
                        dense ? 'py-2' : 'py-3',
                        c.hideOnMobile && 'hidden md:table-cell',
                        c.className,
                      )}
                    >
                      {c.cell ? c.cell(row) : (row as Record<string, ReactNode>)[c.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
